'use client';

import { DirectionProvider } from '@base-ui/react/direction-provider';
import { Slider as SliderPrimitive } from '@base-ui/react/slider';
import { Angry, Frown, Laugh, type LucideIcon, Meh, Smile } from 'lucide-react';
import {
  forwardRef,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useId,
  useMemo,
  useState,
} from 'react';
import {
  type FieldProps,
  FieldTooltip,
  hasFieldProps,
  splitFieldProps,
} from '@/components/atoms/forms/_shared';
import {
  Field,
  FieldBottom,
  FieldBottomRight,
  FieldControl,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldTop,
  FieldTopRight,
} from '@/components/ui/form-field';
import { Label } from '@/components/ui/label';
import {
  colorClasses,
  type SliderColor,
  type SliderDirection,
  type SliderSize,
  trackSizeClasses,
  useAutoDirection,
} from '@/components/ui/slider';
import {
  getSliderBounds,
  getSliderValueCount,
  getThumbCount,
  normalizeSliderValues,
} from '@/components/ui/slider-utils';
import { cn } from '@/lib/utils';

export type { SliderColor, SliderDirection, SliderSize };
export type SliderLabelMode = 'none' | 'bottom' | 'floating';
export type SliderMode = 'single' | 'range';
export type SliderVariant =
  | 'default'
  | 'with-sub-label'
  | 'with-numbers'
  | 'radio'
  | 'with-percentages'
  | 'with-icon'
  | 'with-icon-and-number'
  | 'with-badge'
  | 'with-badge-range'
  | 'rounded-avatar'
  | 'square-avatar'
  | 'simple-line'
  | 'gradient'
  | 'emojis';

export interface SliderMark {
  /** Numeric position of the mark within the slider bounds. */
  value: number;
  /** Visible label rendered at the mark position. */
  label: string;
}

type SliderGetAriaValueText = (
  formattedValue: string,
  value: number,
  index: number,
) => string;

interface SliderControlProps
  extends Omit<SliderPrimitive.Root.Props, 'onValueChange'> {
  /** Direction for RTL support - auto-detected from DOM if not provided */
  dir?: SliderDirection;
  /** Slider mode: "single" for one handle, "range" for two handles */
  mode?: SliderMode;
  /** Figma-aligned visual/composition variant. */
  variant?: SliderVariant;
  /** Size of the slider track: sm (6px), md (8px), lg (12px) */
  size?: SliderSize;
  /** Color variant of the slider */
  color?: SliderColor;
  /** Label text displayed above the slider (inline, without Field wrapping) */
  label?: string;
  /** Label display mode: none, bottom (under thumb), or floating (tooltip above) */
  labelMode?: SliderLabelMode;
  /** Custom formatter for value display */
  valueFormat?: (value: number) => string;
  /** Custom accessible value text for each thumb. */
  getAriaValueText?: SliderGetAriaValueText;
  /** Show min/max values at slider ends */
  showMinMax?: boolean;
  /** Hint text displayed below the slider */
  hint?: string;
  /** Positioned labels used by sub-label, number, and radio variants. */
  marks?: readonly SliderMark[];
  /** Content displayed before the slider track. */
  startContent?: ReactNode;
  /** Content displayed after the slider track. */
  endContent?: ReactNode;
  /** Custom content rendered inside avatar-style thumbs. */
  thumbContent?: ReactNode;
  /** Called when value changes, with Base UI details for primitive interactions. */
  onValueChange?: (
    value: number[],
    eventDetails?: SliderPrimitive.Root.ChangeEventDetails,
  ) => void;
}

export interface SliderProps
  extends Omit<SliderControlProps, 'label'>,
    FieldProps {}

const SliderTooltip = ({
  id,
  value,
  format,
}: {
  id: string;
  value: number;
  format?: (value: number) => string;
}) => {
  const displayValue = format ? format(value) : `${value}%`;

  return (
    <div
      id={id}
      role="tooltip"
      className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 flex flex-col items-center"
    >
      <div className="bg-foreground px-3 py-1.5 rounded-md">
        <span className="text-xs font-normal text-background whitespace-nowrap">
          {displayValue}
        </span>
      </div>
      <svg
        width="8"
        height="4"
        viewBox="0 0 8 4"
        fill="none"
        className="text-foreground"
        aria-hidden="true"
      >
        <path d="M4 4L0 0H8L4 4Z" fill="currentColor" />
      </svg>
    </div>
  );
};

function getSliderMarkerKey(value: number, markerSlot: number): string {
  return `${markerSlot}:${value}`;
}

const emojiOptions: ReadonlyArray<{
  value: number;
  label: string;
  icon: LucideIcon;
}> = [
  { value: 1, label: 'Angry', icon: Angry },
  { value: 2, label: 'Annoyed', icon: Frown },
  { value: 3, label: 'Neutral', icon: Meh },
  { value: 4, label: 'Happy', icon: Smile },
  { value: 5, label: 'Delighted', icon: Laugh },
];

type SliderThumbTreatment =
  | 'default'
  | 'line'
  | 'gradient'
  | 'rounded-avatar'
  | 'square-avatar'
  | 'hidden';

interface SliderVariantConfig {
  adornment: 'default' | 'badge';
  defaultLabelMode: SliderLabelMode;
  defaultMode: SliderMode;
  defaultValue?: number;
  discreteMarks: boolean;
  emojiTrack: boolean;
  gradientTrack: boolean;
  markTicks?: true;
  marksVisible: boolean;
  percentageEndpoints: boolean;
  spacious: boolean;
  thumb: SliderThumbTreatment;
}

const sliderVariantConfig: Record<SliderVariant, SliderVariantConfig> = {
  default: {
    adornment: 'default',
    defaultLabelMode: 'none',
    defaultMode: 'single',
    discreteMarks: false,
    emojiTrack: false,
    gradientTrack: false,
    marksVisible: false,
    percentageEndpoints: false,
    spacious: false,
    thumb: 'default',
  },
  'with-sub-label': {
    adornment: 'default',
    defaultLabelMode: 'none',
    defaultMode: 'single',
    discreteMarks: false,
    emojiTrack: false,
    gradientTrack: false,
    marksVisible: true,
    percentageEndpoints: false,
    spacious: false,
    thumb: 'default',
  },
  'with-numbers': {
    adornment: 'default',
    defaultLabelMode: 'none',
    defaultMode: 'single',
    discreteMarks: false,
    emojiTrack: false,
    gradientTrack: false,
    marksVisible: true,
    percentageEndpoints: false,
    spacious: false,
    thumb: 'default',
  },
  radio: {
    adornment: 'default',
    defaultLabelMode: 'none',
    defaultMode: 'single',
    discreteMarks: true,
    emojiTrack: false,
    gradientTrack: false,
    markTicks: true,
    marksVisible: true,
    percentageEndpoints: false,
    spacious: false,
    thumb: 'default',
  },
  'with-percentages': {
    adornment: 'default',
    defaultLabelMode: 'floating',
    defaultMode: 'single',
    discreteMarks: false,
    emojiTrack: false,
    gradientTrack: false,
    marksVisible: false,
    percentageEndpoints: true,
    spacious: false,
    thumb: 'default',
  },
  'with-icon': {
    adornment: 'default',
    defaultLabelMode: 'floating',
    defaultMode: 'single',
    discreteMarks: false,
    emojiTrack: false,
    gradientTrack: false,
    marksVisible: false,
    percentageEndpoints: false,
    spacious: true,
    thumb: 'default',
  },
  'with-icon-and-number': {
    adornment: 'default',
    defaultLabelMode: 'floating',
    defaultMode: 'single',
    discreteMarks: false,
    emojiTrack: false,
    gradientTrack: false,
    marksVisible: false,
    percentageEndpoints: false,
    spacious: false,
    thumb: 'default',
  },
  'with-badge': {
    adornment: 'badge',
    defaultLabelMode: 'floating',
    defaultMode: 'single',
    discreteMarks: false,
    emojiTrack: false,
    gradientTrack: false,
    marksVisible: false,
    percentageEndpoints: false,
    spacious: false,
    thumb: 'default',
  },
  'with-badge-range': {
    adornment: 'badge',
    defaultLabelMode: 'floating',
    defaultMode: 'range',
    discreteMarks: false,
    emojiTrack: false,
    gradientTrack: false,
    marksVisible: false,
    percentageEndpoints: false,
    spacious: false,
    thumb: 'default',
  },
  'rounded-avatar': {
    adornment: 'default',
    defaultLabelMode: 'none',
    defaultMode: 'single',
    discreteMarks: false,
    emojiTrack: false,
    gradientTrack: false,
    marksVisible: false,
    percentageEndpoints: false,
    spacious: false,
    thumb: 'rounded-avatar',
  },
  'square-avatar': {
    adornment: 'default',
    defaultLabelMode: 'none',
    defaultMode: 'single',
    discreteMarks: false,
    emojiTrack: false,
    gradientTrack: false,
    marksVisible: false,
    percentageEndpoints: false,
    spacious: false,
    thumb: 'square-avatar',
  },
  'simple-line': {
    adornment: 'default',
    defaultLabelMode: 'none',
    defaultMode: 'single',
    discreteMarks: false,
    emojiTrack: false,
    gradientTrack: false,
    marksVisible: false,
    percentageEndpoints: false,
    spacious: false,
    thumb: 'line',
  },
  gradient: {
    adornment: 'default',
    defaultLabelMode: 'none',
    defaultMode: 'single',
    discreteMarks: false,
    emojiTrack: false,
    gradientTrack: true,
    marksVisible: false,
    percentageEndpoints: false,
    spacious: false,
    thumb: 'gradient',
  },
  emojis: {
    adornment: 'default',
    defaultLabelMode: 'none',
    defaultMode: 'single',
    defaultValue: 3,
    discreteMarks: true,
    emojiTrack: true,
    gradientTrack: false,
    marksVisible: false,
    percentageEndpoints: false,
    spacious: false,
    thumb: 'hidden',
  },
};

const sliderThumbClasses = {
  default: 'size-4 rounded-full border bg-background',
  line: 'h-5 w-1.5 rounded-md border-2 border-primary-foreground bg-primary',
  gradient: 'size-5 rounded-full border-2 border-background bg-background',
  'rounded-avatar':
    'size-5 overflow-hidden rounded-full border border-primary bg-background',
  'square-avatar':
    'size-5 overflow-hidden rounded border border-primary bg-background',
  hidden:
    'size-8 rounded-full border-0 bg-transparent opacity-0 shadow-none focus-visible:opacity-100 focus-visible:ring-2',
} as const satisfies Record<SliderThumbTreatment, string>;

function getUniformMarkStep(
  marks: readonly SliderMark[] | undefined,
): number | undefined {
  if (!marks || marks.length < 2) return undefined;
  const values = [...new Set(marks.map((mark) => mark.value))].sort(
    (a, b) => a - b,
  );
  const step = values[1] - values[0];
  if (step <= 0) return undefined;
  return values.every(
    (value, index) => index === 0 || value - values[index - 1] === step,
  )
    ? step
    : undefined;
}

function getNearestMarkValue(
  value: number,
  marks: readonly SliderMark[],
): number {
  return marks.reduce(
    (nearest, mark) =>
      Math.abs(mark.value - value) < Math.abs(nearest - value)
        ? mark.value
        : nearest,
    marks[0].value,
  );
}

/**
 * Base slider component with support for single/range modes
 */
const SliderBase = forwardRef<
  HTMLDivElement,
  Omit<SliderPrimitive.Root.Props, 'onValueChange'> & {
    size?: SliderSize;
    color?: SliderColor;
    variant?: SliderVariant;
    thumbCount?: number;
    thumbContent?: ReactNode;
    getAriaValueText?: SliderGetAriaValueText;
    onThumbKeyDown?: (
      event: KeyboardEvent<HTMLDivElement>,
      thumbIndex: number,
    ) => void;
    onValueChange?: (
      value: number | readonly number[],
      eventDetails?: SliderPrimitive.Root.ChangeEventDetails,
    ) => void;
  }
>(
  (
    {
      className,
      defaultValue,
      value,
      min = 0,
      max = 100,
      orientation = 'horizontal',
      size = 'md',
      color = 'primary',
      variant = 'default',
      thumbCount,
      thumbContent,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      getAriaValueText,
      onThumbKeyDown,
      onValueChange,
      ...props
    },
    forwardedRef,
  ) => {
    // Derive thumb count from value/defaultValue/min if not explicitly provided
    const count = getThumbCount(
      thumbCount ??
        getSliderValueCount(value) ??
        getSliderValueCount(defaultValue) ??
        1,
    );

    const colors = colorClasses[color];
    const config = sliderVariantConfig[variant];
    const normalizedValues =
      normalizeSliderValues(value ?? defaultValue, min, max) ?? [];
    const selectedEmojiValue = normalizedValues[0];

    return (
      <SliderPrimitive.Root
        ref={forwardedRef}
        className="data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full"
        data-slot="slider"
        data-color={color}
        data-size={size}
        data-variant={variant}
        defaultValue={defaultValue}
        value={value}
        min={min}
        max={max}
        orientation={orientation}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        onValueChange={onValueChange}
        {...props}
      >
        <SliderPrimitive.Control
          data-slot="slider-control"
          className={cn(
            'group/slider data-[orientation=vertical]:min-h-40 relative flex w-full touch-none items-center select-none data-disabled:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col',
            className,
          )}
        >
          <SliderPrimitive.Track
            data-slot="slider-track"
            className={cn(
              'rounded-full data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full relative overflow-hidden select-none',
              config.emojiTrack
                ? 'data-[orientation=horizontal]:h-8 data-[orientation=vertical]:w-8'
                : trackSizeClasses[size],
              config.emojiTrack ? 'bg-transparent' : colors.track,
            )}
          >
            <SliderPrimitive.Indicator
              data-slot="slider-range"
              data-gradient={config.gradientTrack ? 'true' : undefined}
              className={cn(
                'select-none data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full',
                config.gradientTrack
                  ? cn(
                      'from-[var(--slider-gradient-start)] to-[var(--slider-gradient-end)]',
                      orientation === 'vertical'
                        ? 'bg-gradient-to-t'
                        : 'bg-gradient-to-r rtl:bg-gradient-to-l',
                    )
                  : colors.indicator,
                config.emojiTrack && 'hidden',
              )}
            />
            {config.emojiTrack && (
              <div
                className="absolute inset-1 flex items-center justify-center gap-6"
                aria-hidden="true"
                data-slot="slider-emoji-options"
              >
                {emojiOptions.map((option) => {
                  const Icon = option.icon;
                  const selected = selectedEmojiValue === option.value;

                  return (
                    <span
                      key={option.value}
                      data-selected={selected ? 'true' : undefined}
                      className={cn(
                        'flex size-6 items-center justify-center rounded-full text-muted-foreground',
                        selected &&
                          'bg-primary/10 text-primary ring-2 ring-primary ring-offset-1 ring-offset-background',
                      )}
                    >
                      <Icon className="size-4" />
                    </span>
                  );
                })}
              </div>
            )}
          </SliderPrimitive.Track>
          {Array.from(
            { length: count },
            (_, thumbSlot) => `thumb-${thumbSlot}`,
          ).map((thumbId, thumbIndex) => (
            <SliderPrimitive.Thumb
              data-slot="slider-thumb"
              data-thumb-variant={variant}
              key={thumbId}
              getAriaLabel={ariaLabel ? () => ariaLabel : undefined}
              getAriaValueText={getAriaValueText}
              aria-labelledby={ariaLabelledBy}
              aria-describedby={ariaDescribedBy}
              onKeyDownCapture={(event) => onThumbKeyDown?.(event, thumbIndex)}
              className={cn(
                'relative shadow-sm transition-[color,box-shadow] duration-150 ease-out hover:ring-4 focus-visible:ring-4 data-[dragging]:shadow-md focus-visible:outline-hidden flex shrink-0 items-center justify-center select-none disabled:pointer-events-none disabled:opacity-50 motion-reduce:transition-none before:absolute before:-inset-3.5 before:rounded-full',
                sliderThumbClasses[config.thumb],
                colors.thumb,
              )}
            >
              {thumbContent && !config.emojiTrack && (
                <span
                  className={cn(
                    'pointer-events-none flex size-full items-center justify-center overflow-hidden',
                    config.thumb === 'rounded-avatar'
                      ? 'rounded-full'
                      : config.thumb === 'square-avatar'
                        ? 'rounded-[3px]'
                        : 'rounded-[inherit]',
                    '[&>img]:size-full [&>img]:object-cover',
                  )}
                >
                  {thumbContent}
                </span>
              )}
            </SliderPrimitive.Thumb>
          ))}
        </SliderPrimitive.Control>
      </SliderPrimitive.Root>
    );
  },
);

SliderBase.displayName = 'SliderBase';

const SliderControl = forwardRef<HTMLDivElement, SliderControlProps>(
  (
    {
      mode = 'single',
      variant = 'default',
      size,
      color = 'primary',
      label,
      labelMode,
      valueFormat,
      showMinMax,
      hint,
      marks,
      startContent,
      endContent,
      thumbContent,
      min,
      max,
      step,
      value,
      defaultValue,
      className,
      onValueChange,
      dir,
      orientation = 'horizontal',
      getAriaValueText,
      ...props
    },
    forwardedRef,
  ) => {
    const { ref, direction } = useAutoDirection(dir);
    const tooltipIdBase = useId();
    const config = sliderVariantConfig[variant];
    const resolvedSize =
      size ??
      (variant === 'rounded-avatar' ||
      variant === 'square-avatar' ||
      variant === 'simple-line' ||
      variant === 'gradient'
        ? 'lg'
        : variant === 'default'
          ? 'md'
          : 'sm');
    const markValues = marks?.map((mark) => mark.value);
    const requestedMin =
      min ??
      (config.discreteMarks && markValues?.length
        ? Math.min(...markValues)
        : config.emojiTrack
          ? 1
          : 0);
    const requestedMax =
      max ??
      (config.discreteMarks && markValues?.length
        ? Math.max(...markValues)
        : config.emojiTrack
          ? 5
          : 100);
    const bounds = useMemo(
      () => getSliderBounds(requestedMin, requestedMax),
      [requestedMax, requestedMin],
    );
    const safeMin = bounds.min;
    const safeMax = bounds.max;
    const resolvedMode =
      mode === 'single' && config.defaultMode === 'range'
        ? config.defaultMode
        : mode;
    const resolvedLabelMode = labelMode ?? config.defaultLabelMode;
    const resolvedMarks = useMemo(
      () =>
        config.emojiTrack
          ? emojiOptions.map(({ label: emojiLabel, value: emojiValue }) => ({
              label: emojiLabel,
              value: emojiValue,
            }))
          : marks,
      [config.emojiTrack, marks],
    );
    const resolvedStep =
      step ??
      (config.discreteMarks ? getUniformMarkStep(resolvedMarks) : undefined);

    // Compute default value based on mode
    const computedDefaultValue = useMemo(() => {
      if (value !== undefined) return undefined; // controlled, don't use defaultValue
      const defaultArr = normalizeSliderValues(defaultValue, safeMin, safeMax);
      if (defaultArr !== undefined) return defaultArr;
      if (config.defaultValue !== undefined) return [config.defaultValue];
      return resolvedMode === 'range' ? [safeMin, safeMax] : [safeMin];
    }, [
      config.defaultValue,
      defaultValue,
      resolvedMode,
      safeMin,
      safeMax,
      value,
    ]);

    const [internalValue, setInternalValue] = useState<number[]>(
      normalizeSliderValues(value, safeMin, safeMax) ??
        computedDefaultValue ??
        (resolvedMode === 'range' ? [safeMin, safeMax] : [safeMin]),
    );

    const controlledValue = useMemo(
      () => normalizeSliderValues(value, safeMin, safeMax),
      [value, safeMin, safeMax],
    );
    const rawCurrentValue = controlledValue ?? internalValue;
    const currentValue = useMemo(
      () => normalizeSliderValues(rawCurrentValue, safeMin, safeMax) ?? [],
      [rawCurrentValue, safeMin, safeMax],
    );

    const handleValueChange = useCallback(
      (
        newValue: number | readonly number[],
        eventDetails?: SliderPrimitive.Root.ChangeEventDetails,
      ) => {
        const normalized =
          normalizeSliderValues(newValue, safeMin, safeMax) ?? [];
        const arr =
          config.discreteMarks && resolvedMarks?.length
            ? normalized.map((sliderValue) =>
                getNearestMarkValue(sliderValue, resolvedMarks),
              )
            : normalized;
        if (value === undefined) {
          setInternalValue(arr);
        }
        onValueChange?.(arr, eventDetails);
      },
      [
        config.discreteMarks,
        onValueChange,
        resolvedMarks,
        safeMin,
        safeMax,
        value,
      ],
    );

    const handleThumbKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>, thumbIndex: number) => {
        if (!config.discreteMarks || !resolvedMarks?.length) return;

        const sortedValues = [
          ...new Set(resolvedMarks.map((mark) => mark.value)),
        ].sort((a, b) => a - b);
        const current = currentValue[thumbIndex] ?? sortedValues[0];
        const currentIndex = sortedValues.reduce(
          (nearestIndex, markValue, markIndex) =>
            Math.abs(markValue - current) <
            Math.abs(sortedValues[nearestIndex] - current)
              ? markIndex
              : nearestIndex,
          0,
        );
        const horizontalIncrement =
          direction === 'rtl'
            ? event.key === 'ArrowLeft'
              ? 1
              : -1
            : event.key === 'ArrowRight'
              ? 1
              : -1;
        const increment =
          event.key === 'ArrowUp'
            ? 1
            : event.key === 'ArrowDown'
              ? -1
              : event.key === 'ArrowLeft' || event.key === 'ArrowRight'
                ? horizontalIncrement
                : 0;
        const nextIndex =
          event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? sortedValues.length - 1
              : Math.min(
                  sortedValues.length - 1,
                  Math.max(0, currentIndex + increment),
                );

        if (increment === 0 && event.key !== 'Home' && event.key !== 'End') {
          return;
        }

        event.preventDefault();
        const nextValue = [...currentValue];
        nextValue[thumbIndex] = sortedValues[nextIndex];
        handleValueChange(nextValue);
      },
      [
        config.discreteMarks,
        currentValue,
        direction,
        handleValueChange,
        resolvedMarks,
      ],
    );

    const formatValue = useCallback(
      (val: number) => {
        if (valueFormat) return valueFormat(val);
        return `${val}%`;
      },
      [valueFormat],
    );

    // Calculate thumb positions as percentages
    const getThumbPosition = useCallback(
      (val: number) =>
        Math.min(
          100,
          Math.max(0, ((val - safeMin) / (safeMax - safeMin)) * 100),
        ),
      [safeMax, safeMin],
    );

    const valueMarkerTranslate =
      direction === 'rtl' ? 'translateX(50%)' : 'translateX(-50%)';

    const valueMarkers = useMemo(
      () =>
        currentValue.map((markerValue, markerSlot) => ({
          value: markerValue,
          slot: markerSlot,
        })),
      [currentValue],
    );

    const containerHeight =
      resolvedLabelMode === 'bottom'
        ? 'h-14'
        : resolvedLabelMode === 'floating'
          ? 'h-6'
          : 'h-6';

    const resolvedGetAriaValueText =
      getAriaValueText ??
      ((_formattedValue: string, sliderValue: number) => {
        const matchingMark = resolvedMarks?.find(
          (mark) => mark.value === sliderValue,
        );
        return matchingMark?.label ?? formatValue(sliderValue);
      });

    const renderMarks =
      orientation === 'horizontal' &&
      config.marksVisible &&
      marks &&
      marks.length > 0;

    const resolvedStartContent =
      startContent ??
      (config.percentageEndpoints ? formatValue(safeMin) : undefined);
    const resolvedEndContent =
      endContent ??
      (config.percentageEndpoints ? formatValue(safeMax) : undefined);

    const renderAdornment = (content: ReactNode, slot: 'start' | 'end') => {
      if (content === undefined || content === null) return null;

      return (
        <div
          data-slot={`slider-${slot}-content`}
          className={cn(
            'flex shrink-0 items-center gap-2 text-sm text-muted-foreground',
            config.adornment === 'badge' &&
              'rounded-md bg-primary/10 px-1.5 py-0.5 text-xs text-primary',
          )}
        >
          {content}
        </div>
      );
    };

    return (
      <div
        ref={ref}
        className={cn(
          'flex gap-2',
          orientation === 'horizontal'
            ? 'w-full flex-col'
            : 'h-full w-fit flex-row',
          (config.spacious || resolvedLabelMode === 'floating') && 'gap-4',
          className,
        )}
      >
        <DirectionProvider direction={direction}>
          {label && <Label className="text-sm font-medium">{label}</Label>}
          <div
            className={cn(
              'flex min-w-0 items-center gap-3',
              orientation === 'horizontal' ? 'w-full' : 'h-full flex-col',
            )}
          >
            {renderAdornment(resolvedStartContent, 'start')}
            <div
              className={cn(
                'min-w-0',
                orientation === 'horizontal'
                  ? 'w-full flex-1'
                  : 'h-full min-h-40 w-6',
              )}
            >
              <div
                className={cn(
                  'relative',
                  orientation === 'horizontal'
                    ? cn('w-full', containerHeight)
                    : 'h-full min-h-40 w-6',
                )}
              >
                {orientation === 'horizontal' &&
                  resolvedLabelMode === 'floating' &&
                  valueMarkers.map((marker) => (
                    <div
                      key={marker.slot}
                      className="absolute top-2 z-10"
                      style={{
                        insetInlineStart: `${getThumbPosition(marker.value)}%`,
                        transform: valueMarkerTranslate,
                      }}
                    >
                      <SliderTooltip
                        id={`${tooltipIdBase}-tooltip-${marker.slot}`}
                        value={marker.value}
                        format={valueFormat}
                      />
                    </div>
                  ))}

                <div
                  className={cn(
                    'absolute',
                    orientation === 'horizontal'
                      ? cn('inset-x-0', 'top-2')
                      : 'inset-y-0 start-2',
                  )}
                >
                  <SliderBase
                    ref={forwardedRef}
                    min={safeMin}
                    max={safeMax}
                    step={resolvedStep}
                    size={resolvedSize}
                    color={color}
                    variant={variant}
                    orientation={orientation}
                    thumbCount={currentValue.length}
                    thumbContent={thumbContent}
                    value={currentValue}
                    defaultValue={computedDefaultValue}
                    getAriaValueText={resolvedGetAriaValueText}
                    onThumbKeyDown={handleThumbKeyDown}
                    onValueChange={handleValueChange}
                    {...props}
                  />
                </div>

                {orientation === 'horizontal' &&
                  resolvedLabelMode === 'bottom' &&
                  valueMarkers.map((marker) => (
                    <div
                      key={marker.slot}
                      className="absolute bottom-0"
                      style={{
                        insetInlineStart: `${getThumbPosition(marker.value)}%`,
                        transform: valueMarkerTranslate,
                      }}
                    >
                      <span
                        data-slot="slider-value"
                        className="text-base font-medium text-foreground whitespace-nowrap"
                      >
                        {formatValue(marker.value)}
                      </span>
                    </div>
                  ))}
              </div>

              {renderMarks && (
                <div
                  className={cn(
                    'relative w-full text-sm text-muted-foreground',
                    config.markTicks ? 'h-9' : 'h-5',
                  )}
                  data-slot="slider-marks"
                >
                  {marks.map((mark, markIndex) => {
                    const position = getThumbPosition(mark.value);
                    const isStart = position === 0;
                    const isEnd = position === 100;

                    return (
                      <div
                        key={getSliderMarkerKey(mark.value, markIndex)}
                        data-slot="slider-mark"
                        className={cn(
                          'absolute top-0 flex flex-col',
                          isStart
                            ? 'items-start text-start'
                            : isEnd
                              ? 'items-end text-end -translate-x-full rtl:translate-x-full'
                              : 'items-center text-center',
                        )}
                        style={{
                          insetInlineStart: `${position}%`,
                          transform:
                            isStart || isEnd ? undefined : valueMarkerTranslate,
                        }}
                      >
                        {config.markTicks && (
                          <span
                            data-slot="slider-tick"
                            className="mb-1 h-3 w-px bg-border"
                            aria-hidden="true"
                          />
                        )}
                        <span className="whitespace-nowrap">{mark.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {renderAdornment(resolvedEndContent, 'end')}
          </div>
          {showMinMax && (
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{safeMin}</span>
              <span>{safeMax}</span>
            </div>
          )}
          {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
        </DirectionProvider>
      </div>
    );
  },
);

SliderControl.displayName = 'SliderControl';

// =============================================================================
// Slider — public API with smart field mode
// =============================================================================

const Slider = forwardRef<HTMLDivElement, SliderProps>(
  (props, forwardedRef) => {
    const [fieldPropsObj, controlProps] = splitFieldProps(props);

    if (!hasFieldProps(fieldPropsObj)) {
      const inlineLabel =
        typeof fieldPropsObj.label === 'string'
          ? fieldPropsObj.label
          : undefined;

      // In bare mode, pass label through as the control's inline label
      return (
        <SliderControl
          ref={forwardedRef}
          {...(controlProps as Omit<SliderControlProps, 'label'>)}
          aria-label={controlProps['aria-label'] ?? inlineLabel}
          label={fieldPropsObj.label}
        />
      );
    }

    const {
      label,
      required,
      labelIcon,
      tooltip,
      topRight,
      description,
      bottomRight,
      error,
      fieldClassName,
    } = fieldPropsObj;
    const cp = controlProps as Omit<SliderControlProps, 'label'>;
    const accessibleLabel =
      cp['aria-label'] ?? (typeof label === 'string' ? label : undefined);
    const hasTopRow = label || topRight;
    const hasBottomRow = description || bottomRight || error;

    return (
      <Field
        state={error ? 'destructive' : undefined}
        disabled={cp.disabled}
        className={fieldClassName}
      >
        {hasTopRow && (
          <FieldTop>
            {label && (
              <FieldLabel required={required} labelIcon={labelIcon}>
                {label}
                {tooltip && <FieldTooltip content={tooltip} />}
              </FieldLabel>
            )}
            {topRight && <FieldTopRight>{topRight}</FieldTopRight>}
          </FieldTop>
        )}
        <FieldControl>
          {() => (
            <SliderControl
              ref={forwardedRef}
              {...cp}
              aria-label={accessibleLabel}
            />
          )}
        </FieldControl>
        {hasBottomRow && (
          <FieldBottom>
            {error && <FieldError>{error}</FieldError>}
            {!error && description && (
              <FieldDescription>{description}</FieldDescription>
            )}
            {bottomRight && (
              <FieldBottomRight className="ms-auto">
                {bottomRight}
              </FieldBottomRight>
            )}
          </FieldBottom>
        )}
      </Field>
    );
  },
);

Slider.displayName = 'Slider';

export { Slider, SliderBase };
